import { forwardRef, useState } from 'react';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { cn } from '@/utils/helpers';

/**
 * Reusable Input component with variants and validation states
 */
const Input = forwardRef(({
  label,
  type = 'text',
  error,
  success,
  hint,
  className,
  containerClassName,
  icon: Icon,
  iconPosition = 'left',
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

  const hasError = Boolean(error);
  const hasSuccess = Boolean(success);

  const inputStyles = cn(
    'w-full px-4 py-3 rounded-lg border bg-white text-gray-900 placeholder-gray-400',
    'transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:border-transparent',
    Icon && iconPosition === 'left' && 'pl-11',
    Icon && iconPosition === 'right' && 'pr-11',
    isPassword && 'pr-11',
    hasError && 'border-red-300 focus:ring-red-500',
    hasSuccess && 'border-green-300 focus:ring-green-500',
    !hasError && !hasSuccess && 'border-gray-200 focus:ring-primary-500',
    className
  );

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      
      <div className="relative">
        {Icon && iconPosition === 'left' && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Icon className="w-5 h-5" />
          </div>
        )}
        
        <input
          ref={ref}
          type={inputType}
          className={inputStyles}
          {...props}
        />

        {Icon && iconPosition === 'right' && !isPassword && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Icon className="w-5 h-5" />
          </div>
        )}

        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}

        {hasError && !isPassword && !Icon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">
            <AlertCircle className="w-5 h-5" />
          </div>
        )}

        {hasSuccess && !isPassword && !Icon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
            <CheckCircle className="w-5 h-5" />
          </div>
        )}
      </div>

      {(error || success || hint) && (
        <p className={cn(
          'text-sm',
          hasError && 'text-red-500',
          hasSuccess && 'text-green-500',
          !hasError && !hasSuccess && 'text-gray-500'
        )}>
          {error || success || hint}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
